/** "Remove from approval…" from the Visit side: record surfaces, board row menu and board bulk run the approval's own withdrawal. */
import { computed, inject, Injector } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ActivityEntityName, JobSecurityRight, VisitApprovalCommandId, VisitApprovalScope, VisitApprovalStanding, VisitApprovalStandingStatus, VisitApprovalSummary, VisitBrowseRow } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { ApprovalWithdrawalGroup, MAX_APPROVAL_REMOVALS, withdrawFromApprovals } from '@workbuddy/entities/visit.approval/commands/approval.remove.visits.command';
import { reloadApproval } from '@workbuddy/entities/visit.approval/commands/approval.command.support';
import { EntityDataService, EntityDataServiceRaw } from '@workbuddy/services/entities';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import type { BulkCommandArgs, BulkCommandOutcome, EntityBoardBulkCommandsArgs } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { ActionCommandCapability } from '@workbuddy/ui/v2/surface.engine';
import { visitSurfaceModel } from '../visit.model';

const LABEL = 'Remove from approval…';
export const VISIT_REMOVE_FROM_APPROVAL_BULK_ID = 'visit.removeFromApproval';
// Neither has a membership to withdraw: auto-approval is the line's policy, changed on the line.
const WITHOUT_MEMBERSHIP: ReadonlySet<VisitApprovalStandingStatus | undefined> = new Set([VisitApprovalStandingStatus.NotApproved, VisitApprovalStandingStatus.AutoApproved]);

type RemoveVisits = (rows: readonly VisitBrowseRow[]) => Promise<boolean>;

export function visitRemoveFromApprovalCommand(hostReadonly = false): ActionCommandCapability<VisitBrowseRow> {

  const injector = inject(Injector);
  const rights = inject(RightsService);
  const data = inject(EntityDataService);
  const raw = inject(EntityDataServiceRaw);
  // Record reads carry live standing, so the surface can require a known approval rather than guess.
  const allowed = (row: VisitBrowseRow) => !hostReadonly && !!row.approval?._id && removable(row) && canManage(rights);
  return {
    descriptor: ({ page }) => computed(() => ({ id: 'removeFromApproval', label: LABEL, iconLeft: 'fa-regular fa-stamp', pin: 'never', hidden: !allowed(page.$model()), disabled: page.$saving() || page.$dirty() })),
    run: async page => {
      const row = page.$model();
      if (!allowed(row) || page.$saving() || page.$dirty()) return;
      await removeVisitsFromApproval(injector, [row], async () => {
        const updated = await firstValueFrom(raw.getById<VisitBrowseRow>(ActivityEntityName.Visit, row._id));
        page.reset({ ...visitSurfaceModel(updated.item) });
        await refreshVisitBoards(data);
      });
    },
  };
}

/** Board row menu. List rows carry no approval standing, so the live read at run time decides membership. */
export function visitRemoveFromApprovalRowCommands(row: VisitBrowseRow): Command[] {

  if (!removable(row) || !canManage(inject(RightsService))) return [];
  const remove = boardRemover();
  return [new Command({ _id: 'removeFromApproval', label: LABEL, iconLeft: 'fa-regular fa-stamp', action: () => { void remove([row]); } })];
}

export function visitRemoveFromApprovalBulkCommands(args: EntityBoardBulkCommandsArgs<VisitBrowseRow>): Command[] {

  const rows = (args.selectedItems ?? []).filter(removable);
  if (!rows.length || rows.length > MAX_APPROVAL_REMOVALS || !canManage(inject(RightsService))) return [];
  return [new Command({ _id: VISIT_REMOVE_FROM_APPROVAL_BULK_ID, label: LABEL, iconLeft: 'fa-regular fa-stamp', data: { run: boardRemover() } })];
}

export async function runVisitRemoveFromApprovalBulk(args: BulkCommandArgs<VisitBrowseRow>): Promise<BulkCommandOutcome | false> {

  const run = (args.commandData as { run?: RemoveVisits } | undefined)?.run;
  if (args.action !== VISIT_REMOVE_FROM_APPROVAL_BULK_ID || !run) return false;
  return { handled: true, clearSelection: await run(args.data.filter(removable)) };
}

/** Job-less and not known to be outside an approval: a Job's execution record supersedes the attendance it spawned. */
function removable(row: VisitBrowseRow): boolean {

  return !row.job?._id && !!row.customer?._id && !!row.occurrenceId && !WITHOUT_MEMBERSHIP.has(row.approvalStatus);
}

function canManage(rights: RightsService): boolean {

  return rights.hasRights(JobSecurityRight.CanAccess) && rights.hasRights(JobSecurityRight.Manage);
}

/** Stamped in the board's injection context; the menu action and bulk handler run outside it. */
function boardRemover(): RemoveVisits {

  const injector = inject(Injector);
  const data = inject(EntityDataService);
  const toasts = inject(ToastService);
  return async rows => {
    try {
      return await removeVisitsFromApproval(injector, rows, () => refreshVisitBoards(data));
    } catch (error) {
      toasts.error('Remove from approval', error instanceof Error ? error.message : 'The approval could not be read.');
      return false;
    }
  };
}

async function refreshVisitBoards(data: EntityDataService): Promise<void> {

  data.invalidate(ActivityEntityName.Visit);
  await data.invalidateItems(ActivityEntityName.UpcomingWork);
}

/**
 * Resolves each Visit's current membership, then runs one withdrawal per approval.
 *
 * The live read is required, not defensive: the Withdraw request must quote the membership's evidence
 * and the approval's revision, and no Visit read carries either — a stale pair is a 409.
 */
async function removeVisitsFromApproval(injector: Injector, rows: readonly VisitBrowseRow[], refresh: () => Promise<void>): Promise<boolean> {

  if (!rows.length || rows.length > MAX_APPROVAL_REMOVALS) return false;
  const data = injector.get(EntityDataService);
  const standings = await Promise.all(rows.map(async row => ({
    row,
    standing: await data.executeCommand<VisitApprovalStanding>(ActivityEntityName.VisitApproval, VisitApprovalCommandId.Current, { occurrenceId: row.occurrenceId, scope: visitApprovalScope([row]) }),
  })));
  const byApproval = new Map<string, { rows: VisitBrowseRow[]; memberships: ApprovalWithdrawalGroup['memberships'][number][] }>();
  for (const { row, standing } of standings) {
    const approvalId = standing?.approval?._id;
    if (!approvalId || !standing.approvalEvidence || WITHOUT_MEMBERSHIP.has(standing.approvalStatus)) continue;
    const group = byApproval.get(approvalId) ?? { rows: [], memberships: [] };
    group.rows.push(row);
    group.memberships.push({ occurrenceId: row.occurrenceId, evidence: standing.approvalEvidence });
    byApproval.set(approvalId, group);
  }
  const toasts = injector.get(ToastService);
  if (!byApproval.size) {
    toasts.warning('Remove from approval', rows.length === 1 ? 'This visit is not in an approval.' : 'None of the selected visits is in an approval.');
    return false;
  }
  const groups = await Promise.all([...byApproval].map(async ([approvalId, group]): Promise<ApprovalWithdrawalGroup> => {
    const scope = visitApprovalScope(group.rows);
    const approval = await data.executeCommand<VisitApprovalSummary>(ActivityEntityName.VisitApproval, VisitApprovalCommandId.Get, { approvalId, scope });
    return { approval, scope, memberships: group.memberships };
  }));
  const committed = await withdrawFromApprovals(injector, { groups, reload: () => reloadApproval(injector, { changed: refresh }) });
  const skipped = rows.length - groups.reduce((sum, group) => sum + group.memberships.length, 0);
  if (committed && skipped) toasts.info('Remove from approval', `${skipped} of the selected visits ${skipped === 1 ? 'was' : 'were'} not in an approval and ${skipped === 1 ? 'was' : 'were'} left unchanged.`);
  return committed;
}

/** The open-approval scope, narrowed to a site or plan only when every Visit in the group shares it. */
function visitApprovalScope(rows: readonly VisitBrowseRow[]): VisitApprovalScope {

  const [first] = rows;
  const shared = (pick: (row: VisitBrowseRow) => string | undefined) => rows.every(row => pick(row) === pick(first)) ? pick(first) : undefined;
  return { customerId: first.customer!._id, siteId: shared(row => row.site?._id), servicePlanId: shared(row => row.servicePlan?._id) };
}
